import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import jsonToFormData from 'json-form-data';
import { useTranslation } from 'react-i18next';

import boardSetupSchema from '../boardSetup.schema';
import useForm from '../../../lib/hooks/useForm';
import { useGetEditBoardSettings, updateBoardSettings } from '../data';
import { useNotification } from '../../../shared/Notification/useNotification';
import { snakeCaseKeys } from '../../../shared/helpers';

const useEditBoardSettings = () => {
  const { t } = useTranslation('boardSetup');
  const [postSubmitCallback, setPostSubmitCallback] = useState(null);
  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const queryClient = useQueryClient();
  const { showSuccess } = useNotification();
  const { mutate } = useMutation(updateBoardSettings, {
    onSuccess: ({ data }) => {
      queryClient.setQueryData(['editDemo'], data);
      showSuccess({
        content: t('editSuccess'),
        title: t('settingsTitle'),
      });
      if (postSubmitCallback) {
        const callbackToRun = postSubmitCallback;
        setPostSubmitCallback(null);
        callbackToRun();
      }
    },
  });
  const { uuid, boardSettings } = useGetEditBoardSettings();
  const initialValues = {
    ...boardSettings,
    logo: { name: '' },
    customReplyEmailName: boardSettings.customReplyEmailName || '',
    publicSlug: boardSettings.publicSlug || '',
    name: boardSettings.name || '',
    email: boardSettings.email || '',
    timezone: boardSettings.timezone || '',
    persistentMessage: boardSettings.persistentMessage || '',
    primaryColor: boardSettings.primaryColor || '',
    customInviteEmailSubject: boardSettings.customInviteEmailSubject || '',
    guestUserConversionModal: !!boardSettings.guestUserConversionModal,
    hideWelcomeModalSignIn: !!boardSettings.hideWelcomeModalSignIn,
    isPublic: !!boardSettings.isPublic,
    useEmployeeId: !!boardSettings.useEmployeeId,
    employeeIdBody: boardSettings.employeeIdBody || '',
    employeeIdHeader:
      boardSettings.employeeIdHeader || t('sharing.employeeIdHeaderDefault'),
  };

  const onSubmit = (values, { setErrors }) => {
    const parsedValues = snakeCaseKeys(values);

    const formData = jsonToFormData(parsedValues);
    mutate(
      { demo: formData, id: uuid },
      {
        onError: error => {
          const response = error && error.response;
          const errors = response && response.data && response.data.errors;
          if (errors) {
            const errorMessages = Object.entries(errors).reduce((acc, curr) => {
              const key = curr[0];
              return {
                ...acc,
                [key]: `${t(`formLabel.${key}`)} ${curr[1]}`,
              };
            }, []);
            setErrors(errorMessages);
          }
          // Reset any stale navigation intent so later successful saves don't trigger it
          setPostSubmitCallback(null);
          setPendingAction(null);
        },
      },
    );
  };

  const form = useForm({
    onSubmit,
    validationSchema: boardSetupSchema,
    enableReinitialize: true,
    initialValues,
  });

  const submitAndThen = callback => {
    setPostSubmitCallback(() => callback);
    form.handleSubmit();
  };

  // Normal save should not carry over a previous leave-intent callback
  const handleSubmit = () => {
    if (postSubmitCallback || pendingAction) {
      setPostSubmitCallback(null);
      setPendingAction(null);
    }
    form.handleSubmit();
  };

  const handleCloseModal = () => {
    setShowUnsavedChangesModal(false);
    setPendingAction(null);
  };

  const handleDiscardChanges = () => {
    setShowUnsavedChangesModal(false);
    if (pendingAction) {
      pendingAction();
      setPendingAction(null);
    }
  };

  const handleSaveAndLeave = () => {
    if (pendingAction) {
      setShowUnsavedChangesModal(false);
      submitAndThen(() => {
        pendingAction();
        setPendingAction(null);
      });
    } else {
      form.handleSubmit();
    }
  };

  return {
    ...form,
    initialValues,
    handleSubmit,
    submitAndThen,
    showUnsavedChangesModal,
    setShowUnsavedChangesModal,
    pendingAction,
    setPendingAction,
    handleCloseModal,
    handleDiscardChanges,
    handleSaveAndLeave,
  };
};

export default useEditBoardSettings;
